<template>
  <div class="h-shelltop">
    <div id="topBar" class="h-topbar">
      <div class="h-topbar__brand">
        <router-link class="h-topbar__home" to="/admin" aria-label="Hermes, home">
          <span class="h-topbar__mark" aria-hidden="true">H</span>
          <span class="h-topbar__word">Hermes</span>
        </router-link>
        <HermesTenantSwitcher :vendor-name="tenantName" :can-switch="canSwitchBusiness" />
      </div>

      <nav class="h-topbar__nav" aria-label="Main">
        <router-link
          v-for="item in navItems"
          :key="item.href"
          class="h-topbar__link"
          :to="item.href"
          :aria-current="item.href === section ? 'page' : null"
          @click="reloadIfCurrent(item.href)"
        >{{ item.title }}</router-link>
      </nav>

      <div class="h-topbar__right">
        <span class="h-topbar__mode" :class="`h-topbar__mode--${mode}`">{{ modeLabel }}</span>
        <HermesThemeToggle />

        <div class="h-topbar__user" ref="userRoot">
          <button
            type="button"
            class="h-topbar__user-btn"
            :aria-expanded="open ? 'true' : 'false'"
            aria-haspopup="menu"
            :title="userName"
            @click.stop="open = !open"
          >
            <span class="h-topbar__avatar">{{ userInitials || 'U' }}</span>
            <svg width="14" height="14" viewBox="0 0 24 24" class="h-topbar__caret" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
          </button>
          <div v-if="open" class="h-topbar__menu" role="menu">
            <div class="h-topbar__menu-head">
              <div class="h-topbar__menu-name">{{ userName }}</div>
              <div class="h-topbar__menu-role">{{ userRole }}</div>
            </div>
            <a
              v-for="(item, i) in accountItems"
              :key="i"
              class="h-topbar__menu-item"
              :href="item.href"
              role="menuitem"
            >{{ item.title }}</a>
          </div>
        </div>
      </div>
    </div>

    <p v-if="modeNotice" class="h-shelltop__band" role="status">{{ modeNotice }}</p>
    <HermesBreadcrumbs v-if="crumbs" class="h-shelltop__crumbs" :items="crumbs" />
  </div>
</template>

<script>
// Hermes override of @apexl/icarus-core's structure/TheTopMenuBar.vue: one calm bar
// with the five destinations, the business, the payment mode and the user menu.
//
// The server threads `navItems` (filtered to this person's permissions) and the rest
// onto this component in app\module\shell\Hook\ShellChromeHook, on every page. The
// stock TitleTile is ignored. The logout link comes from the account
// SimpleDropdownMenu the server still supplies.
import { useRoute } from 'vue-router';
import useApi from '@apexl/icarus-core/src/services/api';
import { useTenantStore } from '@root/store/useTenantStore';

const PAYMENTS = '/admin/transactions';
const CUSTOMERS = '/admin/customers';
const PRODUCTS = '/admin/catalogue';
const SETTINGS = '/admin/business';

// The destination a screen sits under. Anything else sits under Settings.
const SECTIONS = [
  ['/admin/payment-requests', PAYMENTS],
  [PAYMENTS, PAYMENTS],
  ['/admin/subscriptions', CUSTOMERS],
  [CUSTOMERS, CUSTOMERS],
  [PRODUCTS, PRODUCTS],
  ['/admin/products', PRODUCTS],
  ['/admin/plans', PRODUCTS],
];

const TO_SETTINGS = { label: 'Settings', to: SETTINGS };
const TO_PAYMENTS = { label: 'Payments', to: PAYMENTS };
const TO_CUSTOMERS = { label: 'Customers', to: CUSTOMERS };

// Screens below a destination. A destination itself needs no trail.
const CRUMBS = {
  '/admin/payment-requests': [TO_PAYMENTS, { label: 'Payment requests' }],
  '/admin/subscriptions':    [TO_CUSTOMERS, { label: 'Subscriptions' }],
  '/admin/reports':          [TO_SETTINGS, { label: 'Reporting' }],
  '/admin/usage':            [TO_SETTINGS, { label: 'Usage' }],
  '/admin/integrate':        [TO_SETTINGS, { label: 'Set up a product' }],
  '/admin/users':            [TO_SETTINGS, { label: 'Team' }],
  '/admin/roles':            [TO_SETTINGS, { label: 'Roles' }],
  '/admin/vendor':           [TO_SETTINGS, { label: 'VAT' }],
  '/admin/settings':         [TO_SETTINGS, { label: 'My settings' }],
};

const MODE_LABELS = { test: 'Test', live: 'Live', mixed: 'Test and live', none: 'Not connected' };

// A label of what the connections take, not a filter: test and live payments are not
// separated in the data, so nothing here hides or narrows what a screen shows.
const MODE_NOTICES = {
  test: "You're in test mode. New payments use test cards and no real money moves.",
  mixed: 'Some of your products take real payments and some are in test mode.',
};

// io-user 6 removed /user/settings; Hermes's own page replaces it.
const MY_SETTINGS = { title: 'My settings', href: '/admin/settings' };

export default {
  name: 'TheTopMenuBar',
  props: {
    // The server-built topbar children (TitleTile row + account SimpleDropdownMenu).
    components:        { type: Array,   default: () => [] },
    navItems:          { type: Array,   default: () => [] },
    vendorName:        { type: String,  default: '' },
    canSwitchBusiness: { type: Boolean, default: false },
    paymentMode:       { type: String,  default: 'none' },
    userName:          { type: String,  default: '' },
    userInitials:      { type: String,  default: '' },
    userRole:          { type: String,  default: '' },
  },
  setup() {
    return { route: useRoute(), tenantStore: useTenantStore() };
  },
  data() {
    return { open: false, api: useApi() };
  },
  computed: {
    // The server's name wins: it follows a switch of business, and the store may
    // still hold the last one.
    tenantName() {
      return this.vendorName || this.tenantStore.vendorName || 'Your business';
    },
    mode() {
      return this.tenantStore.paymentMode || this.paymentMode;
    },
    modeLabel() {
      return MODE_LABELS[this.mode] || MODE_LABELS.none;
    },
    modeNotice() {
      return MODE_NOTICES[this.mode] || '';
    },
    section() {
      const path = this.route.path;
      if (path === '/' || path === '/admin') return '/admin';
      const match = SECTIONS.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`));
      return match ? match[1] : SETTINGS;
    },
    crumbs() {
      const path = this.route.path;
      if (CRUMBS[path]) return CRUMBS[path];

      const sub = path.match(/^\/admin\/subscriptions\/(.+)$/);
      if (sub) {
        return [TO_CUSTOMERS, { label: 'Subscriptions', to: '/admin/subscriptions' }, { label: `#${sub[1]}` }];
      }
      return null;
    },
    // Pull the account menu items (logout etc.) out of the server-supplied
    // SimpleDropdownMenu so the compact menu keeps working.
    accountItems() {
      const found = this.findByName(this.components, 'SimpleDropdownMenu');
      const items = (found?.props?.menuItems || [])
        .map(i => ({ title: i.title, href: i.href }))
        // Drop any link to the settings page io-user no longer has.
        .filter(i => !String(i.href || '').includes('/user/settings'));
      return [MY_SETTINGS, ...items];
    },
  },
  watch: {
    paymentMode: {
      immediate: true,
      handler(mode) {
        this.tenantStore.setPaymentMode(mode);
      },
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocClick);
    this.syncVendorName();
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocClick);
  },
  methods: {
    // Keep the tenant store populated, for a page whose build carries no name.
    syncVendorName() {
      if (this.vendorName) {
        this.tenantStore.setVendorName(this.vendorName);
        return;
      }
      if (this.tenantStore.vendorName) return;
      this.api.get('/api/v1/admin/summary')
        .then((summary) => this.tenantStore.setVendorName(summary?.vendor_name))
        .catch(() => { /* leave empty — the label falls back */ });
    },
    // Parity with core MenuLink: refresh when navigating to the already-active route.
    reloadIfCurrent(href) {
      if (href === this.route.path) this.$router.go();
    },
    onDocClick(e) {
      if (this.$refs.userRoot && !this.$refs.userRoot.contains(e.target)) {
        this.open = false;
      }
    },
    findByName(nodes, name) {
      for (const n of nodes || []) {
        if (!n) continue;
        if (n.name === name) return n;
        const nested = this.findByName(n.components, name);
        if (nested) return nested;
      }
      return null;
    },
  },
};
</script>

<style lang="scss">
.h-shelltop {
  width: 100%;

  &__band {
    margin: 0;
    padding: 8px 20px;
    background: var(--h-test-bg, #FEF3C7);
    color: var(--h-test-text, #92400E);
    font-family: var(--h-font-body);
    font-size: 13.5px;
    font-weight: 500;
  }

  &__crumbs { padding: 12px 32px 0; }
}

#topBar.h-topbar {
  height: auto; // Core's stylesheet still compiles and fixes it at 80px.
  min-height: var(--h-topbar-height, 56px);
  box-shadow: none;
  background: var(--h-bg-card, #fff);
  border-bottom: 1px solid var(--h-border, #E2E8F0);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 20px;
  padding: 8px 20px;
  width: 100%;
  font-family: var(--h-font-body);

  .h-topbar__brand { display: flex; align-items: center; gap: 12px; min-width: 0; }

  .h-topbar__home {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
    font-size: 15px;
    letter-spacing: -0.01em;
    color: var(--h-text-primary, #0F172A);
    text-decoration: none;

    &:focus-visible { outline: none; box-shadow: var(--h-focus-ring); border-radius: var(--h-radius, 6px); }
  }

  .h-topbar__mark {
    width: 26px;
    height: 26px;
    flex: none;
    display: grid;
    place-items: center;
    border-radius: var(--h-radius, 6px);
    background: var(--h-accent-hover, #0284C7);
    color: var(--h-text-inverse, #fff);
    font-size: 14px;
  }

  .h-topbar__nav { display: flex; flex-wrap: wrap; gap: 2px; flex: 1; min-width: 0; }

  .h-topbar__link {
    padding: 6px 11px;
    border-radius: var(--h-radius, 6px);
    font-size: 14px;
    font-weight: 500;
    color: var(--h-text-secondary, #64748B);
    text-decoration: none;

    &:hover { background: var(--h-bg-subtle, #F1F5F9); color: var(--h-text-primary, #0F172A); }
    &:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }

    &[aria-current="page"] { background: var(--h-accent-light, #E0F2FE); color: var(--h-accent-dark, #0369A1); }
  }

  .h-topbar__right { margin-left: auto; display: flex; align-items: center; gap: 12px; }

  .h-topbar__mode {
    padding: 3px 10px;
    border-radius: var(--h-radius-pill, 999px);
    font-size: 12.5px;
    font-weight: 600;
    white-space: nowrap;
    background: var(--h-neutral-bg, #F1F5F9);
    color: var(--h-neutral-text, #475569);

    &--test, &--mixed { background: var(--h-test-bg, #FEF3C7); color: var(--h-test-text, #92400E); }
    &--live { background: var(--h-success-bg, #ECFDF5); color: var(--h-success-text, #047857); }
  }

  .h-topbar__user { position: relative; }

  .h-topbar__user-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    border: 1px solid var(--h-border-strong, #CBD5E1);
    border-radius: var(--h-radius, 6px);
    background: var(--h-bg-card, #fff);
    padding: 3px 8px 3px 3px;
    cursor: pointer;
    color: var(--h-text-secondary, #64748B);

    &:hover { border-color: var(--h-border, #E2E8F0); }
    &:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
  }

  .h-topbar__avatar {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: var(--h-apexl-gold, #C4A882);
    color: #3A2E17;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  .h-topbar__caret { fill: none; stroke: currentColor; stroke-width: 2; color: var(--h-text-tertiary, #94A3B8); }

  .h-topbar__menu {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    min-width: 200px;
    background: var(--h-bg-card, #fff);
    border: 1px solid var(--h-border, #E2E8F0);
    border-radius: var(--h-radius-lg, 10px);
    box-shadow: var(--h-shadow-panel, 0 4px 16px rgba(15,23,42,.10));
    padding: 6px;
    z-index: 1000;
  }

  .h-topbar__menu-head { padding: 8px 10px 10px; border-bottom: 1px solid var(--h-border, #E2E8F0); margin-bottom: 4px; }
  .h-topbar__menu-name { font-size: 13px; font-weight: 600; color: var(--h-text-primary, #0F172A); }
  .h-topbar__menu-role { font-size: 11px; color: var(--h-text-tertiary, #94A3B8); }

  .h-topbar__menu-item {
    display: block;
    padding: 8px 10px;
    border-radius: var(--h-radius, 6px);
    font-size: 13px;
    color: var(--h-text-primary, #0F172A);
    text-decoration: none;

    &:hover { background: var(--h-bg-subtle, #F1F5F9); color: var(--h-accent-dark, #0369A1); }
    &:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
  }

  // Phone: the destinations take a row of their own under the brand.
  @media (max-width: 720px) {
    padding: 8px 16px;

    .h-topbar__nav { order: 3; flex: 1 1 100%; }
    .h-topbar__word { display: none; }
    .h-topbar__brand { gap: 8px; }
    .h-topbar__right { gap: 8px; }
    .h-topbar__link { padding: 6px 7px; font-size: 13.5px; }
    .h-topbar__mode { padding: 3px 8px; font-size: 12px; }
  }
}

@media (max-width: 720px) {
  .h-shelltop__band { padding: 8px 16px; }
  .h-shelltop__crumbs { padding: 12px 16px 0; }
}
</style>
